import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  TextField,
  Button,
  Box,
  Container,
  CssBaseline,
  Typography,
  Avatar,
  IconButton,
  Paper,
  Grid,
  Checkbox,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { getAccessTokenUsingRefreshToken, loginOnline } from "../../api/login"; // Importing loginOnline function from login API
import { useErrorBoundary } from "react-error-boundary";
import { validateJwtToken } from "../../utils/methods";
import useNetworkStatus from "../../hooks/useNetworStatus";
import { db } from "../../indexDB/model";

/**
 * Login component for user authentication.
 * @param {Object} props - Component props.
 * @param {Function} props.setLoggedIn - Function to update login status.
 * @returns {JSX.Element} - Login component JSX.
 */
export default function Login({ setLoggedIn, setOpenSyncDialog = () => { } }) {
  const { showBoundary } = useErrorBoundary();
  // State hooks for managing component state
  const [showPassword, setShowPassword] = useState(false); // State to toggle password visibility
  const [error, setError] = useState(""); // State to manage error messages
  const [isChecked, setIsChecked] = useState(false); // State to manage checkbox state
  const isOnline = useNetworkStatus()

  // Handle checkbox state change
  const handleCheckboxChange = (event) => {
    setIsChecked(event.target.checked);
  };

  // Get navigation utility from React Router
  const history = useNavigate();

  // Toggle password visibility
  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  useEffect(() => {
    (async () => {
      let validated = false
      const { valid: validAccessToken, expireIn: accessTokenExpiresIn } = validateJwtToken(localStorage.getItem("token"));
      const user = await db.users.where("id").equals(1).first();
      validated = validAccessToken && !!user;
      if (accessTokenExpiresIn < 2) {
        const user = await db.users.where("id").equals(1).first();
        const { valid: validRefreshToken, expireIn: refreshTokenExpiresIn } = validateJwtToken(localStorage.getItem("token"));
        validated = validRefreshToken;
        // Once refresh token implemented use password with refreshToken
        if (isOnline && validRefreshToken) {
          const loginResponse = await getAccessTokenUsingRefreshToken(user.email, user.password);
        }
      }
      if (validated) {
        setLoggedIn(true);
        history("/");
      }
    })();
  }, []);

  /**
   * Handles form submission.
   * @param {Event} e - Form submission event.
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(""); // Clear previous errors

    // Extract email and password from form
    const email = e.currentTarget.querySelector('[name="email"]').value;
    const password = e.currentTarget.querySelector('[name="password"]').value;

    try {
      // Attempt login
      const success = await loginOnline(email, password);
      if (success) {
        setLoggedIn(true); // Set user as logged in
        history("/"); // Redirect to homepage
        setOpenSyncDialog(true)
      } else {
        setLoggedIn(false); // Set user as not logged in
        // Display error message
      }
    } catch (error) {
      console.error(error);
      setLoggedIn(false);
      showBoundary(error);
      setError("An error occurred while logging in."); // Display error message
    }
  };

  return (
    <ThemeProvider theme={createTheme()}>
      <CssBaseline />

      <Container component="main" maxWidth="xs">
        <Box
          sx={{
            marginTop: 8,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Paper
            elevation={3}
            sx={{
              p: 4,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Avatar sx={{ m: 1, bgcolor: "primary.main" }}>
              <LockOutlinedIcon />
            </Avatar>
            <Typography component="h1" variant="h5">
              Sign in
            </Typography>

            <Box
              component="form"
              onSubmit={handleSubmit}
              noValidate
              sx={{ mt: 1 }}
            >
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                autoFocus
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type={showPassword ? "text" : "password"}
                id="password"
                autoComplete="current-password"
                InputProps={{
                  endAdornment: (
                    <IconButton onClick={toggleShowPassword} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  ),
                }}
              />
              <Grid
                item
                xs={12}
                sx={{ display: "flex", alignItems: "center", mb: 1 }}
              >
                <Checkbox
                  checked={isChecked}
                  onChange={handleCheckboxChange}
                  sx={{ margin: 0 }}
                />
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: { xs: "10px", md: "12px" },
                    color: "#404040",
                    fontFeatureSettings: "'clig' off, 'liga' off",
                    fontStyle: "normal",
                    fontWeight: 400,
                    lineHeight: "normal",
                    letterSpacing: "0.2px",
                    marginLeft: "0.5rem",
                  }}
                >
                  I agree to the{" "}
                  <a
                    href="https://docs.google.com/document/d/1NvP-vSEx826MlbBRafGaZoxoW5DIyrYPid8odr-9DyA"
                    target="_blank"
                  >
                    Terms & Conditions
                  </a>{" "}
                  and the{" "}
                  <a
                    href="https://docs.google.com/document/d/1PFIMUJi4KqawetOVZhM-eRU_OCUsJzz9Mn0uF8XkxxI"
                    target="_blank"
                  >
                    Privacy Policy
                  </a>
                  .
                </Typography>
              </Grid>
              {error && (
                <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                  {error}
                </Typography>
              )}
              <Button
                disabled={!isChecked}
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mb: 2 }}
              >
                Sign In
              </Button>
            </Box>
          </Paper>
        </Box>
      </Container>
    </ThemeProvider>
  );
}
